import { ref } from 'vue'
import { API, HOST } from '@/utils/http'

// CloudFront signed-cookie session for direct browser → CloudFront → S3 tile
// reads (ADR-0016). apiv3 sets HttpOnly CloudFront-* cookies scoped to the
// active org's tile prefix; the browser then attaches them automatically to
// same-origin /s3/lyr/... tile fetches. JS only tracks { orgId, expiresAt } —
// enough to schedule refresh and to gate tile URLs during the org-switch gap
// (the cookies themselves are HttpOnly and never read here).
//
// Refresh fires ~5 min before expiry. On logout / org switch the snapshot is
// dropped so tileUrl/transformRequest stop emitting the previous org's tiles.

const session = ref(null) // { orgId, expiresAt }

let refreshTimer = null
let activeOrgId = null

// transformRequest / tileUrl read this synchronously on the hot path.
export function getS3SessionSnapshot() {
  return session.value
}

function scheduleRefresh(expiresAtSec) {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
  // Refresh 5 minutes before expiry, with a 60s floor.
  const ms = Math.max((expiresAtSec * 1000) - Date.now() - 5 * 60 * 1000, 60_000)
  refreshTimer = setTimeout(() => {
    refreshS3Session(activeOrgId).catch(e => console.error('S3 cookie refresh failed', e))
  }, ms)
}

export async function refreshS3Session(orgId) {
  if (!orgId) return null
  const isOrgSwitch = activeOrgId !== null && activeOrgId !== orgId
  activeOrgId = orgId

  // On org switch, drop the snapshot immediately so tiles for the new org
  // aren't requested until its cookies are set (the old org's cookies wouldn't
  // authorize the new prefix anyway → 403 storm during the gap).
  if (isOrgSwitch) {
    session.value = null
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null }
  }

  try {
    // withCredentials so the Set-Cookie response is stored and cookies ride the
    // request. Same-origin today, but explicit for safety.
    const { data } = await API.post(`${HOST}/org/${orgId}/s3/cookies`, null, { withCredentials: true })
    // Discard late responses: the active org changed mid-request.
    if (activeOrgId !== orgId) return null
    session.value = { orgId, expiresAt: data.expires_at }
    scheduleRefresh(data.expires_at)
    return session.value
  } catch (err) {
    // Any failure leaves callers with "no session" so tiles fall back / skip
    // rather than 403; the next refresh repopulates.
    if (activeOrgId === orgId) session.value = null
    console.error('refreshS3Session failed', err)
    return null
  }
}

export function clearS3Session() {
  session.value = null
  activeOrgId = null
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

// Bind the session lifecycle to the selected-org getter so this module is the
// single owner of "cookies match the active org." Returns the store.watch
// unsubscribe handle for HMR / teardown.
export function bindS3SessionToOrg(store) {
  // The selected org is restored from localStorage before auth completes, so gate
  // on the session too — otherwise this fires an authenticated request at boot and
  // then never re-fires (the org id doesn't change once login lands).
  const activeOrg = getters => getters.getIsLoggedIn ? getters['org/getSelected'] : null

  const initial = activeOrg(store.getters)
  if (initial) refreshS3Session(String(initial))

  return store.watch(
    (_state, getters) => activeOrg(getters),
    (newOrgId) => {
      if (newOrgId) refreshS3Session(String(newOrgId))
      else clearS3Session()
    },
  )
}
